import React, {useState, useCallback, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import GA4 from '../../lib/analytics';
import {http} from '../../lib/http/request';
import {auth} from '../../lib/http/auth';
import styles from './login-modal.css';
import closeIcon from './icons/close.svg';

const LoginModal = ({isOpen, onClose = () => {}, onLoginSuccess = () => {}}) => {
    const [qrImage, setQrImage] = useState(null);
    const [sceneId, setSceneId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [pollStatus, setPollStatus] = useState(''); // 'waiting' | 'success' | 'error'
    const [pollMessage, setPollMessage] = useState('');
    const timerRef = useRef(null);

    // ----- 停止轮询 -----
    const stopPolling = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setPollStatus('');
        setPollMessage('');
    }, []);

    // ----- 请求二维码 -----
    const fetchQR = useCallback(async () => {
        // 如果有旧轮询，先停止
        stopPolling();
        setQrImage(null);
        setSceneId(null);
        setError(false);
        setLoading(true);

        try {
            const res = await http.get('/wx/createQRCode', {type: 'FW'});
            if (res.success && res.result?.ticket) {
                const qrUrl = `https://mp.weixin.qq.com/cgi-bin/showqrcode?ticket=${res.result.ticket}`;
                setQrImage(qrUrl);
                setSceneId(res.result.sceneId);
                setPollStatus('waiting');
                setPollMessage('请使用微信扫码登录');
            } else {
                throw new Error('No ticket returned');
            }
        } catch (err) {
            console.error('Failed to fetch QR code:', err); // eslint-disable-line no-console
            setError(true);
            setQrImage(null);
        } finally {
            setLoading(false);
        }
    }, [stopPolling]);

    // ----- 轮询登录状态 -----
    useEffect(() => {
        // 条件：有 sceneId、弹窗打开、且轮询状态为 'waiting'（表示需要轮询）
        if (!sceneId || !isOpen || pollStatus !== 'waiting') {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            return;
        }

        let cancelled = false;

        const poll = async () => {
            if (cancelled) return;
            try {
                const result = await auth.loginPoll(sceneId);
                if (cancelled) return;

                // 假设登录成功时 result 包含 success: true 或 code: 0
                if (result !== null && typeof result !== 'undefined') {
                    // 登录成功
                    setPollStatus('success');
                    setPollMessage('登录成功！');
                    stopPolling();
                    onLoginSuccess();
                    onClose(); // 关闭弹窗
                } else {
                    // 未登录，继续轮询
                    timerRef.current = setTimeout(poll, 2000);
                }
            } catch (err) {
                if (cancelled) return;
                console.error('Poll error:', err); // eslint-disable-line no-console
                setPollStatus('error');
                setPollMessage('登录检查失败，请刷新重试');
                stopPolling();
                // 可设置 error 状态显示错误
                setError(true);
            }
        };

        // 立即执行第一次轮询
        timerRef.current = setTimeout(poll, 0);

        return () => {
            cancelled = true;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [sceneId, isOpen, pollStatus, stopPolling, onLoginSuccess, onClose]);

    // ----- 弹窗打开/关闭管理 -----
    useEffect(() => {
        if (isOpen) {
            fetchQR();
        } else {
            // 关闭弹窗时清理轮询和状态
            stopPolling();
            setQrImage(null);
            setSceneId(null);
            setError(false);
            setPollStatus('');
            setPollMessage('');
        }
    }, [isOpen, fetchQR, stopPolling]);

    // ----- 关闭处理 -----
    const handleClose = useCallback(() => {
        GA4.event({
            category: 'close_login_modal'
        });
        stopPolling();
        onClose();
    }, [onClose, stopPolling]);

    // ----- 刷新二维码 -----
    const handleRefresh = useCallback(() => {
        fetchQR();
    }, [fetchQR]);

    if (!isOpen) return null;

    // 判断二维码是否可点击刷新（加载中或登录成功时禁用）
    const isQrClickable = !loading && pollStatus !== 'success';

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={handleClose}
            className={styles['login-modal-container']}
            overlayClassName={styles['login-modal-overlay']}
            shouldCloseOnOverlayClick
            shouldCloseOnEsc
        >
            <div className={styles['modal-header']}>
                <div
                    className={styles['brand-mark']}
                    aria-hidden="true"
                >
                    <span className={`${styles['brand-block']} ${styles['block-blue']}`} />
                    <span className={`${styles['brand-block']} ${styles['block-yellow']}`} />
                    <span className={`${styles['brand-block']} ${styles['block-green']}`} />
                    <span className={`${styles['brand-block']} ${styles['block-pink']}`} />
                </div>
                <button
                    className={styles['close-button']}
                    onClick={handleClose}
                    aria-label="关闭登录弹窗"
                >
                    <img
                        className={styles['close-icon']}
                        src={closeIcon}
                        alt="close"
                    />
                </button>
            </div>

            <div className={styles['scan-qr']}>
                <div className={styles['title-block']}>
                    <p className={styles['eyebrow-text']}>{'少儿编程空间'}</p>
                    <h2 className={styles['welcome-text']}>{'欢迎来到跃动空间'}</h2>
                    <p className={styles['subtitle-text']}>{'用微信扫码，马上开始创作'}</p>
                </div>

                <div className={styles['qr-card']}>
                    <div className={styles['qr-wrapper']}>
                        {loading && <p className={styles['loading-text']}>{'加载二维码中...'}</p>}
                        {!loading && error && (
                            <p className={styles['error-text']}>{'加载失败，请刷新重试'}</p>
                        )}
                        {!loading && !error && qrImage && (
                            <img
                                className={styles['qr-image']}
                                src={qrImage}
                                alt="微信登录二维码"
                                onClick={isQrClickable ? handleRefresh : null}
                                style={{cursor: isQrClickable ? 'pointer' : 'default'}}
                            />
                        )}
                        {!loading && !error && !qrImage && (
                            <p className={styles['loading-text']}>{'暂无二维码'}</p>
                        )}
                    </div>
                </div>

                <p className={styles['hint-text']}>
                    {'微信扫码关注 '}
                    <strong>{'【航硕网络科技】'}</strong>
                    {' 即可快速登录'}
                </p>

                <div className={styles['status-slot']}>
                    {/* 轮询状态提示 */}
                    {pollStatus === 'waiting' && (
                        <p className={styles['status-text']}>
                            {pollMessage}
                        </p>
                    )}
                    {pollStatus === 'success' && (
                        <p className={`${styles['status-text']} ${styles.success}`}>
                            {pollMessage}
                        </p>
                    )}
                    {pollStatus === 'error' && (
                        <p className={`${styles['status-text']} ${styles.error}`}>
                            {pollMessage}
                        </p>
                    )}
                </div>

                <div className={styles['action-row']}>
                    <button
                        type="button"
                        className={styles['phone-login-link']}
                        disabled
                    >
                        {'手机号登录'}
                    </button>
                </div>
            </div>
        </ReactModal>
    );
};

LoginModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onLoginSuccess: PropTypes.func // 登录成功回调，接收用户数据
};

export default LoginModal;
